import localforage from 'localforage';

interface MediaExport {
  id: string;
  type: 'image' | 'video';
  originalSrc?: string;
  filename: string;
  blob?: Blob;
}

// Helper to export current About page media for static hosting
export const exportAboutMedia = async (): Promise<{ 
  exports: MediaExport[], 
  mapping: { [key: string]: { type: 'image' | 'video', src: string } } 
}> => {
  const exports: MediaExport[] = [];
  const mapping: { [key: string]: { type: 'image' | 'video', src: string } } = {};

  try {
    const saved = JSON.parse(localStorage.getItem('aboutPageEdits') || '{}');
    let counter = 1;

    for (const [storageKey, entry] of Object.entries(saved)) {
      const editData = entry as any;
      if (!editData?.elementId) continue;

      const id = editData.elementId;
      
      if (editData.type === 'video') {
        let filename = `about-video-${counter}.mp4`;
        let blob: Blob | null = null;

        // Try to get video blob from localforage
        if (editData.videoKey) {
          try {
            blob = await localforage.getItem<Blob>(editData.videoKey);
            if (blob) {
              // Create proper filename based on blob type
              const extension = blob.type.includes('webm') ? 'webm' : 'mp4';
              filename = `about-video-${counter}.${extension}`;
            }
          } catch (error) {
            console.warn(`Could not load video blob for ${editData.videoKey}:`, error);
          }
        }

        const mediaExport: MediaExport = {
          id,
          type: 'video',
          originalSrc: editData.src,
          filename,
          blob: blob || undefined
        };

        exports.push(mediaExport);
        mapping[id] = {
          type: 'video',
          src: `/about-media/${filename}`
        };
        counter++;

      } else if (editData.type === 'image' && editData.src) {
        // For images, we'll use the existing src (might be data URL or external URL)
        const extension = editData.src.includes('data:image/png') ? 'png' : 
                         editData.src.includes('data:image/webp') ? 'webp' : 'jpg';
        const filename = `about-image-${counter}.${extension}`;

        // Convert data URL to blob if needed
        let blob: Blob | undefined;
        if (editData.src.startsWith('data:')) {
          try {
            const response = await fetch(editData.src);
            blob = await response.blob();
          } catch (error) {
            console.warn(`Could not convert data URL to blob for ${id}:`, error);
          }
        }

        const mediaExport: MediaExport = {
          id,
          type: 'image',
          originalSrc: editData.src,
          filename,
          blob
        };

        exports.push(mediaExport);
        mapping[id] = {
          type: 'image',
          src: blob ? `/about-media/${filename}` : editData.src
        };
        counter++;
      }
    }

    // Log results for easy copying
    console.log('=== ABOUT PAGE MEDIA EXPORT ===');
    console.log('Mapping for about-media.json:');
    console.log(JSON.stringify(mapping, null, 2));
    console.log('\nExports found:', exports.length);
    
    exports.forEach((exp, index) => {
      console.log(`${index + 1}. ${exp.id} -> ${exp.filename} (${exp.type})`);
      if (exp.blob) {
        console.log(`   Blob size: ${(exp.blob.size / 1024 / 1024).toFixed(2)}MB`);
        // Create download link for the blob
        const url = URL.createObjectURL(exp.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = exp.filename;
        console.log(`   Download: Right-click and save -> ${exp.filename}`);
        document.body.appendChild(a);
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 1000);
      }
    });

    return { exports, mapping };

  } catch (error) {
    console.error('Error exporting about media:', error);
    return { exports: [], mapping: {} };
  }
};

// Add to window for easy access in console
(window as any).exportAboutMedia = exportAboutMedia;